const SPIN_DAMPING = 0.99;
const MIN_VELOCITY_FOR_SPINNING = 0.5; // Minimum spins per second to enter spinning state
const EASE_FACTOR = 0.01; // Easing factor for transitioning to constant speed
const STOPPING_EASE_FACTOR = 0.05; // Easing factor for gradually slowing down when animating to result
const FINAL_STOPPING_EASE_FACTOR = 0.08; // More aggressive easing for final approach
const MIN_STOPPING_SPEED = 0.05; // Minimum speed when stopping (spins per second)
const FINAL_STOP_THRESHOLD = 0.01; // Very small threshold for final stop (radians)
const FINAL_APPROACH_THRESHOLD = 0.05; // Distance where we enter final approach phase (radians)

class Wheel {
  wheelRotation = 0;
  spinsPerSecond = 0;
  targetSpinSpeed = 0;
  targetRotation = null; // Target rotation when stopping at animatingToResult state
  onShouldStartSpinning = null;
  onShouldStopAtResult = null;
  prefersReducedMotion = false;

  constructor({onShouldStartSpinning, onShouldStopAtResult, prefersReducedMotion = false}) {
    this.onShouldStartSpinning = onShouldStartSpinning;
    this.onShouldStopAtResult = onShouldStopAtResult;
    this.prefersReducedMotion = prefersReducedMotion;
  }

  update(timeDelta, cursorState, state) {
    if (this.prefersReducedMotion) {
      // Skip all animations when reduced motion is preferred
      return;
    }

    if (cursorState !== "grabbing") {
      // Notify if we should enter spinning state
      if (state === "ready" && Math.abs(this.spinsPerSecond) >= MIN_VELOCITY_FOR_SPINNING) {
        this.onShouldStartSpinning?.();
      }

      if (state === "spinning") {
        // Ease to constant spinning speed
        const diff = this.targetSpinSpeed - this.spinsPerSecond;
        this.spinsPerSecond += diff * EASE_FACTOR;

        // Once we're close enough, lock to constant speed
        if (Math.abs(diff) < 0.01) {
          this.spinsPerSecond = this.targetSpinSpeed;
        }
      } else if (state === "animatingToResult" && this.targetRotation !== null) {
        // Smoothly stop at target rotation with gradual slowdown
        const rotationDiff = this.targetRotation - this.wheelRotation;
        // Normalize to [-PI, PI] range
        let normalizedDiff = rotationDiff;
        while (normalizedDiff > Math.PI) normalizedDiff -= 2 * Math.PI;
        while (normalizedDiff < -Math.PI) normalizedDiff += 2 * Math.PI;

        // Calculate distance to target
        const distance = Math.abs(normalizedDiff);
        const direction = normalizedDiff > 0 ? -1 : 1;

        if (distance > FINAL_STOP_THRESHOLD) {
          if (distance < FINAL_APPROACH_THRESHOLD) {
            // Final approach phase: very gradual slowdown
            // Use distance-based speed that gets progressively slower
            const normalizedDistance = distance / FINAL_APPROACH_THRESHOLD; // 0-1 as we approach
            // Use a stronger ease-out curve for final approach
            const easeOutFactor = 1 - Math.pow(1 - normalizedDistance, 4); // Quartic ease-out
            const targetSpeed = MIN_STOPPING_SPEED * normalizedDistance * normalizedDistance; // Quadratic decrease

            // Gradually ease towards the target speed with more aggressive easing
            const speedDiff = (direction * targetSpeed) - this.spinsPerSecond;
            this.spinsPerSecond += speedDiff * FINAL_STOPPING_EASE_FACTOR;

            // Apply additional damping as we get very close
            if (distance < FINAL_APPROACH_THRESHOLD * 0.5) {
              this.spinsPerSecond *= 0.98; // Additional damping
            }
          } else {
            // Initial stopping phase: gradual slowdown
            const maxSpeed = 0.3; // Maximum spins per second when starting to stop
            const normalizedDistance = Math.min(distance / (Math.PI * 2), 1); // Normalize to 0-1
            const easeOutFactor = 1 - Math.pow(1 - normalizedDistance, 3); // Ease-out cubic curve
            const targetSpeed = MIN_STOPPING_SPEED + (maxSpeed - MIN_STOPPING_SPEED) * (1 - easeOutFactor);

            // Gradually ease towards the target speed
            const speedDiff = (direction * targetSpeed) - this.spinsPerSecond;
            this.spinsPerSecond += speedDiff * STOPPING_EASE_FACTOR;

            // Ensure we maintain minimum speed until entering final approach
            if (Math.abs(this.spinsPerSecond) < MIN_STOPPING_SPEED && distance > FINAL_APPROACH_THRESHOLD) {
              this.spinsPerSecond = direction * MIN_STOPPING_SPEED;
            }
          }
        } else {
          // Very close to target, smoothly come to a complete stop
          // Apply final damping to bring speed to zero
          this.spinsPerSecond *= 0.95;
          if (Math.abs(this.spinsPerSecond) < 0.001) {
            // Close enough, snap to target and stop
            this.wheelRotation = this.targetRotation;
            this.spinsPerSecond = 0;
            this.onShouldStopAtResult?.();
          }
        }
      } else if (state === "ready" || state === "complete") {
        // Apply damping when not grabbing and the wheel is idle (ready/complete)
        this.spinsPerSecond *= SPIN_DAMPING;
        // Stop spinning when speed is very low
        if (Math.abs(this.spinsPerSecond) < 0.01) {
          this.spinsPerSecond = 0;
        }
      }

      // Update wheel rotation from spin speed
      this.wheelRotation -= this.spinsPerSecond * (timeDelta / 1000) * (2 * Math.PI);
    }
  }

  setRotation(rotation) {
    this.wheelRotation = rotation;
  }

  getRotation() {
    return this.wheelRotation;
  }

  setSpinSpeed(speed) {
    this.spinsPerSecond = speed;
  }

  getSpinSpeed() {
    return this.spinsPerSecond;
  }

  setTargetSpinSpeed(speed) {
    this.targetSpinSpeed = speed;
  }

  setTargetRotation(rotation) {
    this.targetRotation = rotation;
  }

  getTargetRotation() {
    return this.targetRotation;
  }
}

export default Wheel;

